/**
 * The one place the browser talks to Django.
 *
 * Everything else in the app goes through the TanStack Query hooks that wrap
 * this. Nothing constructs a URL or a fetch by hand, because the generated
 * `schema.ts` is what makes a wrong path or a wrong body a compile error.
 *
 * `schema.ts` is generated from the backend's OpenAPI export and committed.
 * Never hand-edit it: run `npm run generate:api` after a backend change.
 */

import createClient, { type Middleware } from "openapi-fetch"

import type { paths } from "./schema"

/** Django's CSRF cookie, set by `GET /api/auth/csrf`. */
function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/)
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"])

/** Raised on a 401 so the router can send the user to login exactly once. */
export class NotAuthenticated extends Error {
  constructor() {
    super("No valid session.")
    this.name = "NotAuthenticated"
  }
}

/** Raised on a 403. The action should not have been offered: it is a screen bug. */
export class NotPermitted extends Error {
  constructor(message = "Your role does not allow this action.") {
    super(message)
    this.name = "NotPermitted"
  }
}

/** A 422 carrying the server's message, which the form maps onto its fields. */
export class ValidationFailed extends Error {
  readonly detail: unknown

  constructor(detail: unknown, message: string) {
    super(message)
    this.name = "ValidationFailed"
    this.detail = detail
  }
}

const session: Middleware = {
  async onRequest({ request }) {
    if (UNSAFE_METHODS.has(request.method)) {
      const token = csrfToken()
      if (token) request.headers.set("X-CSRFToken", token)
    }
    return request
  },

  async onResponse({ response }) {
    // Turned into typed errors here rather than at each call site, so a screen
    // can never accidentally render a 401 as an empty table.
    if (response.status === 401) throw new NotAuthenticated()
    if (response.status === 403) throw new NotPermitted()
    if (response.status === 422) {
      const body = await response.clone().json().catch(() => null)
      throw new ValidationFailed(body, messageFrom(body))
    }
    return response
  },
}

/**
 * Strip pydantic's own error class name off a message written for a person.
 *
 * `config\validators.py` raises `ValueError("This field is required and may not
 * be blank.")`, and pydantic publishes that on the wire as "Value error, This
 * field is required and may not be blank." Those first three words name the
 * server's validation library, and on 2026-08-05 they were being printed under
 * the inputs of forms used by training coordinators.
 *
 * It lives here, at the wire boundary, because this is the one door every
 * server message comes through: `ValidationFailed.message` below is built from
 * the wire's first `msg`, and a dozen screens read that `.message` straight
 * into a banner. Stripping at each of those screens is a list somebody forgets
 * to extend; stripping here covers the next one by omission. The field-by-field
 * walk in `lib\validation-errors.ts` applies the same function to each `msg`.
 *
 * Both of pydantic's prefixes are removed, not only the one seen in the wild: a
 * validator written with a bare `assert` publishes "Assertion failed, "
 * instead, and covering it now is cheaper than discovering it later. Anchored
 * to the start so a sentence merely containing the words is left alone.
 */
export function plainMessage(message: string): string {
  return message.replace(/^(?:Value error|Assertion failed),\s*/i, "")
}

function messageFrom(body: unknown): string {
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>
    if (typeof record.detail === "string") return plainMessage(record.detail)
    // django-ninja's schema failures arrive as {detail: [{loc, msg, type}]}.
    if (Array.isArray(record.detail)) {
      const first = record.detail[0] as { msg?: string } | undefined
      if (first?.msg) return plainMessage(first.msg)
    }
  }
  return "That could not be saved. Please check the highlighted fields."
}

export const api = createClient<paths>({
  // No baseUrl: the generated paths already carry the `/api` prefix, so a
  // call site's string is character-for-character the one in the schema.
  // Same origin in development (Vite proxies /api) and in production (Django
  // serves the built SPA), so the session cookie needs no CORS anywhere.
  credentials: "include",
})

api.use(session)

/** Prime the CSRF cookie. Call once before the first unsafe request. */
export async function primeCsrf() {
  await api.GET("/api/auth/csrf", {})
}

/** A request that failed for a reason the middleware does not turn into a class. */
export class RequestFailed extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = "RequestFailed"
    this.status = status
  }
}

/**
 * Unwrap a response body, throwing when there is none.
 *
 * Load-bearing: `data` is undefined whenever the request failed for a reason
 * the middleware does not already raise, a 500 or a 404. Substituting an empty
 * list or null there turns a server fault into "no results", which is the one
 * failure mode a designed empty state cannot tell the user about.
 */
export function expectBody<T>(
  data: T | undefined,
  response: Response,
  what: string,
): T {
  if (data === undefined) {
    throw new RequestFailed(response.status, requestFailureMessage(what, response.status))
  }
  return data
}

/** The same guard for writes, which have nothing to unwrap. */
export function expectOk(response: Response, what: string) {
  if (!response.ok) {
    throw new RequestFailed(response.status, requestFailureMessage(what, response.status))
  }
  return response
}

/**
 * The one sentence a user reads when a request fails for a reason we cannot
 * explain, and the only place its wording is decided.
 *
 * Three obligations, in this order. It is **business language**: the reader is
 * a training coordinator, so no status line, endpoint or verb from the wire
 * appears in the headline. It is **actionable**: it says what to do next and
 * who to tell, because a dead end with no instruction is what generates the
 * phone call. And it still **carries the number support needs**, in a quiet
 * trailing parenthetical rather than as the headline it used to be ("The
 * server answered 500."), so a coordinator reads a sentence and an
 * administrator can still be told the code over the phone.
 *
 * Deliberately verb-neutral. `expectBody` guards reads *and* writes, so the
 * old "could not be loaded" was wrong on the fifteen write call sites that
 * share it, printing "The import could not be loaded." to somebody who had
 * just pressed Apply. `what` names the thing in full, article included, so the
 * caller owns the noun phrase and this owns the sentence.
 */
export function requestFailureMessage(what: string, status: number) {
  return (
    `Something went wrong with ${what}. Try again, and tell your ` +
    `administrator if it keeps happening. (code ${status})`
  )
}
